import {
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DATE_PICKER_FORMAT } from '../../../../../../../../@constants/date';
import DatePicker from '../../../../../../../../components/DatePicker/DatePicker';
import { FileExcelIcon } from '../../../../../../../../components/SvgIcons/FileExcelIcon';
import { FilePdfIcon } from '../../../../../../../../components/SvgIcons/FilePdfIcon';
import { outputDateValue } from '../../../../../../../../utils/InputOutputDateFormat';
import { FooterExtraButtons } from '../../../../../../components/FooterButtons';
import { ReportLabel } from '../../../../../../components/ReportLabel';
import { FinancialAccountReportFormModel, OrderByEnum } from '../../models';

export const FilterForm: FC<{
  onSubmit: (data: FinancialAccountReportFormModel) => void;
  onReset: () => void;
}> = ({ onSubmit, onReset }) => {
  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors },
    watch,
  } = useFormContext<FinancialAccountReportFormModel>();

  const { t } = useTranslation();

  const openAccounts = watch('is_open');

  return (
    <Box sx={{ p: 0, height: '100%', backgroundColor: 'colors.gray.o10', overflowY: 'scroll' }}>
      <Stack direction={'column'} spacing={0} sx={{ width: '100%', height: '100%' }}>
        <ReportLabel label={t('report.financial_account_page.label')} />
        <Stack direction={'row'} spacing={0}>
          <Stack
            direction={'column'}
            spacing={2}
            sx={{
              p: 3,
            }}
          >
            <Controller
              control={control}
              name="is_open"
              render={({ field }) => (
                <FormControlLabel
                  sx={{ m: 0 }}
                  {...field}
                  checked={field.value ?? false}
                  onChange={(e, checked) => field.onChange(checked)}
                  label={
                    <Typography variant="inter16" fontWeight={500} sx={{ ml: '1rem' }}>
                      Open accounts
                    </Typography>
                  }
                  control={<Switch />}
                />
              )}
            />
            {!openAccounts && (
              <>
                <Controller
                  control={control}
                  name="from"
                  render={({ field }) => (
                    <DatePicker
                      value={field.value}
                      label={'From'}
                      inputFormat={DATE_PICKER_FORMAT}
                      clearable={true}
                      onChange={(date: Date | null) => field.onChange(outputDateValue(date))}
                      renderInput={(params: TextFieldProps) => (
                        <TextField
                          {...params}
                          size="medium"
                          variant="standard"
                          sx={{
                            width: '100%',
                          }}
                          label="From"
                          error={Boolean(errors?.from)}
                          helperText={errors?.from?.message}
                          onKeyDown={e => e.key === 'Enter' && e.preventDefault()}
                          placeholder="mm/dd/yyyy"
                        />
                      )}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="to"
                  render={({ field }) => (
                    <DatePicker
                      value={field.value}
                      label={'To'}
                      onChange={(date: Date | null) => field.onChange(outputDateValue(date))}
                      clearable={true}
                      renderInput={(params: TextFieldProps) => (
                        <TextField
                          {...params}
                          size="medium"
                          variant="standard"
                          sx={{
                            width: '100%',
                          }}
                          label="To"
                          error={Boolean(errors?.to)}
                          helperText={errors?.to?.message}
                          onKeyDown={e => e.key === 'Enter' && e.preventDefault()}
                          placeholder="mm/dd/yyyy"
                        />
                      )}
                    />
                  )}
                />
              </>
            )}

            <Controller
              control={control}
              name="order_by"
              render={({ field }) => (
                <FormControl
                  sx={{
                    pb: 2,
                  }}
                >
                  <InputLabel
                    shrink
                    sx={{
                      mt: 2,
                    }}
                  >
                    Order by
                  </InputLabel>

                  <RadioGroup
                    value={field.value}
                    onChange={e => field.onChange(e.target.value)}
                    row
                    sx={{
                      mt: 2,
                    }}
                  >
                    {Object.values(OrderByEnum).map(type => (
                      <FormControlLabel
                        key={type}
                        value={type}
                        control={<Radio />}
                        label={t(`report.financial_account_page.order_by.${type}`)}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              )}
            />

            <Controller
              control={control}
              name="with_balance"
              render={({ field }) => (
                <FormControlLabel
                  sx={{ m: 0 }}
                  {...field}
                  checked={field.value ?? false}
                  onChange={(e, checked) => field.onChange(checked)}
                  label={
                    <Typography variant="inter16" fontWeight={500} sx={{ ml: '1rem' }}>
                      With balance
                    </Typography>
                  }
                  control={<Switch />}
                />
              )}
            />
          </Stack>
        </Stack>
        {/* <FooterButtons onSubmit={handleSubmit(onSubmit)} onReset={onReset} /> */}
        <FooterExtraButtons
          onSubmit={file_type => {
            setValue('file_type', file_type);
            handleSubmit(onSubmit)();
          }}
          onReset={onReset}
          exportFileTypes={[
            {
              label: 'PDF',
              value: 'pdf',
              icon: <FilePdfIcon sx={{ width: '3rem', height: '3rem' }} />,
            },
            {
              label: 'EXCEL',
              value: 'excel',
              icon: <FileExcelIcon sx={{ width: '3rem', height: '3rem' }} />,
            },
          ]}
        />
      </Stack>
    </Box>
  );
};
